/**
 * Iter122 — Reusable typed-name Electronic Signature panel.
 *
 * Captures: typed_name + optional acknowledgments + on submit records the
 * server-side IP + timestamp via POST /api/esignatures.
 *
 * Props:
 *   documentType  string, one of the backend DOC_TYPES
 *   documentId    string
 *   caseId        optional string
 *   acknowledgments  optional array of {key,label} — user must check each
 *   onSigned      callback(sig)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { FileSignature, ShieldCheck, Loader2, Clock, Globe } from 'lucide-react';

const dt = (v) => v ? new Date(v).toLocaleString() : '';

export default function ESignaturePanel({
  documentType,
  documentId,
  caseId = '',
  acknowledgments = [],
  onSigned,
  compact = false,
}) {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [typedName, setTypedName] = useState('');
  const [checks, setChecks] = useState({});
  const [signers, setSigners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/documents/${encodeURIComponent(documentType)}/${encodeURIComponent(documentId)}/signatures`,
        { headers: H },
      );
      if (r.ok) {
        const d = await r.json();
        setSigners(d.signers || []);
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [documentType, documentId, token]);
  useEffect(() => { load(); }, [load]);

  const allAcked = acknowledgments.every(a => !!checks[a.key]);
  const canSign = typedName.trim().length >= 3 && allAcked && !busy;

  const submit = async () => {
    if (!canSign) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/esignatures`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({
          document_type: documentType,
          document_id: documentId,
          case_id: caseId,
          typed_name: typedName.trim(),
          acknowledgments: acknowledgments.reduce((acc, a) => ({ ...acc, [a.key]: !!checks[a.key] }), {}),
        }),
      });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Signature failed', description: d.detail || 'Error', variant: 'destructive' });
      toast({ title: 'Signed', description: `Recorded ${new Date(d.signed_at).toLocaleString()}` });
      setTypedName(''); setChecks({});
      await load();
      if (onSigned) onSigned(d);
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-lg border border-amber-500/30 bg-[#0F1420] ${compact ? 'p-3' : 'p-4'}`} data-testid="esignature-panel">
      <div className="flex items-center gap-2 mb-3">
        <FileSignature className="w-5 h-5 text-amber-300" />
        <div>
          <div className="text-xs text-amber-300 uppercase tracking-wider font-mono">Electronic Signature</div>
          <div className="text-sm text-white font-semibold">Typed-name signature — logged with timestamp + IP</div>
        </div>
      </div>

      {acknowledgments.length > 0 && (
        <div className="space-y-2 mb-3">
          {acknowledgments.map(a => (
            <label key={a.key} className="flex items-start gap-2 text-xs text-slate-200 cursor-pointer">
              <Checkbox
                checked={!!checks[a.key]}
                onCheckedChange={v => setChecks(prev => ({ ...prev, [a.key]: !!v }))}
                data-testid={`esig-ack-${a.key}`}
              />
              <span>{a.label}</span>
            </label>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">Type your full legal name</label>
          <Input
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
            placeholder="e.g. John A. Smith"
            className="bg-slate-800 border-slate-600 text-white font-serif italic text-lg h-11"
            data-testid="esig-typed-name"
          />
        </div>
        <Button
          onClick={submit}
          disabled={!canSign}
          className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold h-11 disabled:opacity-50"
          data-testid="esig-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}
          Sign Now
        </Button>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">
        By typing your name and clicking Sign Now, you are creating a legally binding electronic
        signature. Your IP address, browser, and the exact timestamp will be recorded.
      </p>

      {signers.length > 0 && (
        <div className="mt-4 border-t border-slate-700 pt-3">
          <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider mb-2">Signers ({signers.length})</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {signers.map(s => (
              <div key={s.id} className="text-xs bg-slate-900/60 border border-slate-800 rounded px-2 py-1.5 flex items-center justify-between gap-2 flex-wrap" data-testid={`esig-signer-${s.id}`}>
                <div className="text-white">
                  <span className="font-serif italic">{s.typed_name}</span>
                  <Badge className="ml-2 text-[9px] bg-slate-700 text-slate-200">{s.signer_role || 'user'}</Badge>
                </div>
                <div className="text-[10px] text-slate-400 flex items-center gap-2">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {dt(s.signed_at)}</span>
                  {s.ip_address && <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {s.ip_address}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
